import { useCallback, type Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
  amrapTestLoad,
  formatWeight,
  fromCanonicalKg,
  plateLoadModeFor,
  type CurrentMax,
  type Exercise,
} from '@heraion/shared';

import { weightUnitLabels } from '../weightUnitLabels';
import { amrapTogglePatch, type AmrapOffer } from './amrapToggle';
import type { LoggerMode } from './loggerMode';
import { formatUnitValue, weightLocale } from './plateCalcMath';
import { useRoundingContextRef } from './state/plateConfig';
import { resolveLoadShape } from './state/resolveLoadShape';
import type { DraftEntry, DraftSet, LoggerAction } from './state/loggerDraftTypes';
import { useLastLoggedSet } from './useLastLoggedSet';
import { useStoredLoadShape } from './useStoredLoadShape';

// #539: the AMRAP chip's tap in the live logger. Works out the test a coach
// would propose (amrapTestLoad), hands the decision to amrapTogglePatch, and
// returns the tip for the chip's balloon. Calculates only where a load can
// be proposed (D7): live logging, a pending set the plan did not prescribe
// as AMRAP, turning the chip on.
export function useAmrapToggle({
  dispatch,
  mode,
  entry,
  entryIdx,
  set,
  setIdx,
  exercise,
  maxMeta,
  t,
}: {
  dispatch: Dispatch<LoggerAction>;
  mode: LoggerMode;
  entry: DraftEntry | null;
  entryIdx: number;
  set: DraftSet | null;
  setIdx: number;
  exercise: Exercise | undefined;
  /** Null or absent until the MAXes have loaded: the last set alone counts. */
  maxMeta: ReadonlyMap<string, CurrentMax> | null | undefined;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): () => string | null {
  const { i18n } = useTranslation();
  const roundingContextRef = useRoundingContextRef();
  const slug = entry?.exerciseId ?? '';
  const storedShapeOf = useStoredLoadShape(slug || undefined);
  const lastLoggedOf = useLastLoggedSet(slug);

  const offerFor = useCallback((e: DraftEntry, s: DraftSet): AmrapOffer => {
    if (e.isBodyweight || exercise?.assisted === true || s.extraNegative) {
      return { kind: 'refusal', reason: 'bodyweight' };
    }
    if (s.calibration === true || s.dropset !== null) {
      return { kind: 'refusal', reason: 'kept' };
    }
    const max = maxMeta?.get(e.exerciseId);
    const load = amrapTestLoad({
      userMax: max?.valueKg,
      ...(max?.source ? { userMaxSource: max.source } : {}),
      ...(max?.setAt ? { userMaxSetAt: max.setAt } : {}),
      lastLogged: lastLoggedOf(),
      unit: s.unit,
      loadable: roundingContextRef.current,
      mode: plateLoadModeFor(
        resolveLoadShape(storedShapeOf(e.exerciseId), exercise?.barLoading),
        exercise?.equipment,
      ),
    });
    if (!load) return { kind: 'refusal', reason: 'noBasis' };
    const locale = weightLocale(i18n);
    const labels = weightUnitLabels(t);
    return {
      kind: 'proposal',
      weight: formatUnitValue(fromCanonicalKg(load.kg, s.unit), s.unit, locale),
      source: load.source,
      basisKg: load.basisKg,
      pct: load.pct,
      load: formatWeight(load.kg, s.unit, locale, labels),
      max: formatWeight(load.basisKg, s.unit, locale, labels),
    };
  }, [exercise, maxMeta, lastLoggedOf, roundingContextRef, storedShapeOf, i18n, t]);

  return useCallback(() => {
    if (!entry || !set || setIdx < 0) return null;
    const calculates =
      mode === 'log' &&
      !set.amrap &&
      !set.amrapPrescribed &&
      set.completedAt === null;
    const { patch, tip } = amrapTogglePatch(
      set,
      mode,
      calculates ? offerFor(entry, set) : null,
    );
    dispatch({ type: 'update_set', entryIdx, setIdx, patch });
    return tip ? t(tip.key, tip.params) : null;
  }, [entry, set, setIdx, entryIdx, mode, dispatch, offerFor, t]);
}
